import { branch, withProps } from 'recompose';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { formatGraphqlProductOnwership } from 'bundles/enroll/utils/entitlementProductOwnershipUtils';
import type { FormattedGraphqlProductOwnership } from 'bundles/enroll/utils/entitlementProductOwnershipUtils';
import GetProductOwnership from 'bundles/enroll/utils/queries/GetProductOwnershipQuery.graphql';
import type {
  GetProductOwnershipQuery,
  GetProductOwnershipQueryVariables,
} from 'bundles/enroll/utils/queries/__generated__/GetProductOwnershipQuery';
import paymentsExperiments from 'bundles/epic/clients/payments';
import Naptime from 'bundles/naptimejs';
import ProductOwnershipsV1 from 'bundles/naptimejs/resources/productOwnerships.v1';
import { SPECIALIZATION, VERIFIED_CERTIFICATE } from 'bundles/payments/common/ProductType';

export type ProductOwnershipsInputProps = {
  productOwnershipsId?: string;
};
export type OptionProps = {
  isNotRequired?: boolean;
};

type InputProps = ProductOwnershipsInputProps;

export type PropsFromWithProductOwnerships = {
  productOwnerships?: FormattedGraphqlProductOwnership;
};

type Props = InputProps & PropsFromWithProductOwnerships;

/** @deprecated Use the useProductOwnerships custom hook instead */
export const withProductOwnerships = <WithProductOwnershipsProps extends InputProps>({
  isNotRequired = false,
}: OptionProps = {}) =>
  branch<InputProps>(
    () => paymentsExperiments.get('useDGSProductOwnership'),
    waitForGraphQL<
      WithProductOwnershipsProps,
      GetProductOwnershipQuery,
      GetProductOwnershipQueryVariables,
      PropsFromWithProductOwnerships
    >(GetProductOwnership, {
      skip: (props) => !user.isAuthenticatedUser() || !props.productOwnershipsId,
      options: ({ productOwnershipsId }) => {
        if (!productOwnershipsId) {
          throw new Error(`expected valid productOwnershipsId`);
        }
        return {
          variables: {
            productOwnershipId: productOwnershipsId,
          },
          context: { clientName: 'gatewayGql' },
        };
      },
      props: ({ data, ownProps }) => {
        return {
          ...ownProps,
          productOwnerships: formatGraphqlProductOnwership(data?.Entitlement?.queryProductOwnership),
        };
      },
    }),
    Naptime.createContainer<Props, ProductOwnershipsInputProps & PropsFromWithProductOwnerships>(
      ({ productOwnershipsId }) => {
        if (productOwnershipsId) {
          return {
            productOwnerships: ProductOwnershipsV1.get(productOwnershipsId, {
              fields: ['s12nCourseOwnerships', 'owns'],
              required: !isNotRequired,
            }),
          };
        } else {
          return {};
        }
      }
    )
  );

type SpecializationIdProps = {
  s12nId: string;
};

export const withSpecializationProductOwnershipsId = withProps<ProductOwnershipsInputProps, SpecializationIdProps>(
  ({ s12nId }) => {
    const productOwnershipsId = user.isAuthenticatedUser()
      ? tupleToStringKey([user.get().id, SPECIALIZATION, s12nId])
      : undefined;
    return { productOwnershipsId };
  }
);

type VerifiedCertificateIdProps = {
  courseId?: string;
};
export const withVerifiedCertificateProductOwnershipsId = withProps<
  ProductOwnershipsInputProps,
  VerifiedCertificateIdProps
>(({ courseId }) => {
  const productOwnershipsId =
    user.isAuthenticatedUser() && courseId
      ? tupleToStringKey([user.get().id, VERIFIED_CERTIFICATE, courseId])
      : undefined;
  return { productOwnershipsId };
});
